import { TFunction } from 'react-i18next';

import { filterPredicateIsTruthy } from 'utility/Check/filterPredicateIsTruthy';

import { Iata } from 'types/Search/airport';
import { AirportValue } from 'types/Search/filter';

import { airportList, ListByCountries } from '../__config/Airports';
import {
    countryCodeMap,
    countryMap,
    countrySortMap,
} from '../__config/mapping';
import { Country, RecommendationProps, Region } from '../types';

/** Get AirportLabel Translation, merge iata codes if whole country selected */
export const getAirportLabelList = (
    value: AirportValue<Iata>,
    t: TFunction,
    withTags = false
): string => {
    if (value.size === 0) return '';
    const processedCountryList: Set<Country> = new Set();

    let additionalLabel;
    let isCountry = false;
    const currentList = [...value]
        .map((code) => {
            const country = airportList[code]?.country;
            if (!processedCountryList.has(country)) {
                if (
                    getCountryAirportCount(country) > 1 &&
                    isFullCountrySelected(value, country)
                ) {
                    processedCountryList.add(country);
                    additionalLabel = `${t('TRANS-all-airports')}`;
                    isCountry = true;
                    return t(countryMap[country]);
                }
                additionalLabel = `${code}`;
                return t(airportList[code]?.label);
            }
        })
        .filter(filterPredicateIsTruthy);

    if (currentList.length > 1) {
        return currentList.join(', ');
    }
    if (withTags) {
        return `${currentList[0]} <span>${additionalLabel}</span>`;
    }

    return `${currentList[0]}${isCountry ? ` - ${additionalLabel}` : ''}`;
};
//get iata code list for given region/country
export const getRegionIataList = (entry: Region, country: Country): Set<Iata> =>
    new Set(
        ListByCountries[country].regionList?.find(
            (region) => entry === region.region
        )?.iataCodeList ?? []
    );

type AirportChipList = Array<{
    code?: Iata;
    country?: Country;
}>;

//get data for rendering chips, summarize countries
export const getAirportChipList = (list: Set<Iata>): AirportChipList => {
    const processedCountryList: Set<Country> = new Set();
    return [...list].reduce((chipList: AirportChipList, code) => {
        const country = airportList[code]?.country;
        if (!processedCountryList.has(country)) {
            if (
                getCountryAirportCount(country) > 1 &&
                isFullCountrySelected(list, country)
            ) {
                processedCountryList.add(country);
                chipList.push({ country: country });
            } else {
                chipList.push({ code: code });
            }
        }
        return chipList;
    }, []);
};
export const getAirportLabel = (code: Iata): string => airportList[code]?.label;

export const getCountryLabel = (t: TFunction, code?: Country): string =>
    code ? t(countryMap[code]) : '';

export const getSortedAirportList = (list: Set<Iata>, t: TFunction): Iata[] =>
    [...list].sort((a: Iata, b: Iata) =>
        t(airportList[a].label).localeCompare(t(airportList[b].label))
    );

export const getRecommendedIataList = (list: RecommendationProps[]): Iata[] =>
    list.map((entry) => entry.airport.code);

export const getCurrentCountryOrder = (countryCode: string): Country[] =>
    countrySortMap[countryCode] ?? countrySortMap.DE;

export const getCountryTransKeyByCode = (countryCode: string): string =>
    countryMap[countryCodeMap[countryCode]];

export const isFullCountrySelected = (
    list: Set<Iata>,
    country: Country
): boolean =>
    [...list].filter((code) => airportList[code]?.country === country)
        .length === getCountryAirportCount(country);

export const getCountryAirportCount = (country: Country): number =>
    ListByCountries[country]?.iataCodeList.size;

//filter out invalid iata codes
export const getCleanedAirportList = (airportValue: AirportValue<Iata>) =>
    new Set([...airportValue].filter((value) => Iata[value]));
